/**
 * WhatsApp Service for sharing game content and invitations
 * Handles cross-platform WhatsApp integration with fallback mechanisms
 */

export interface WhatsAppAvailability {
  hasNativeApp: boolean;
  hasWebAccess: boolean;
  preferredMethod: SharingMethod;
}

export enum SharingMethod {
  WHATSAPP_NATIVE = 'whatsapp-native',
  WHATSAPP_WEB = 'whatsapp-web',
  GENERIC_SHARE = 'generic-share',
  CLIPBOARD_COPY = 'clipboard-copy'
}

export interface ShareData {
  message: string;
  url?: string;
  title?: string;
}

export class WhatsAppService {
  private static instance: WhatsAppService;
  private availability: WhatsAppAvailability | null = null;

  private constructor() {}

  public static getInstance(): WhatsAppService {
    if (!WhatsAppService.instance) {
      WhatsAppService.instance = new WhatsAppService();
    }
    return WhatsAppService.instance;
  }

  /**
   * Detect WhatsApp availability on the current platform
   */
  public detectWhatsAppAvailability(): WhatsAppAvailability {
    if (this.availability) {
      return this.availability;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);
    const isAndroid = /android/i.test(userAgent);

    // Check for native app availability (heuristic)
    const hasNativeApp = isMobile && (isIOS || isAndroid);
    
    // WhatsApp Web is available on desktop and as fallback on mobile
    const hasWebAccess = true;

    // Determine preferred method
    let preferredMethod: SharingMethod;
    if (hasNativeApp) {
      preferredMethod = SharingMethod.WHATSAPP_NATIVE;
    } else if (hasWebAccess) {
      preferredMethod = SharingMethod.WHATSAPP_WEB;
    } else {
      preferredMethod = SharingMethod.GENERIC_SHARE;
    }

    this.availability = {
      hasNativeApp,
      hasWebAccess,
      preferredMethod
    };

    return this.availability;
  }

  /**
   * Share content to WhatsApp using the best available method
   */
  public async shareToWhatsApp(shareData: ShareData): Promise<boolean> {
    try {
      const availability = this.detectWhatsAppAvailability();
      let result = false;
      
      switch (availability.preferredMethod) {
        case SharingMethod.WHATSAPP_NATIVE:
          result = await this.shareToNativeApp(shareData);
          break;
        
        case SharingMethod.WHATSAPP_WEB:
          result = await this.shareToWhatsAppWeb(shareData);
          break;
        
        default:
          result = await this.handleSharingFallback(shareData);
          break;
      }

      // If primary method failed, try fallback
      if (!result) {
        return await this.handleSharingFallback(shareData);
      }

      return result;
    } catch (error) {
      console.error('WhatsApp sharing failed:', error);
      return await this.handleSharingFallback(shareData);
    }
  }

  /**
   * Generate WhatsApp Web URL for sharing
   */
  public generateWhatsAppWebURL(message: string, url?: string): string {
    const baseURL = 'https://web.whatsapp.com/send';
    
    // If URL is provided, append it to the message with two newlines
    let fullMessage = message;
    if (url) {
      fullMessage = `${message}\n\n${url}`;
    }
    
    const encodedMessage = encodeURIComponent(fullMessage);
    
    return `${baseURL}?text=${encodedMessage}`;
  }

  /**
   * Generate WhatsApp mobile deep link URL
   */
  public generateWhatsAppMobileURL(message: string, url?: string): string {
    const baseURL = 'whatsapp://send';
    
    // If URL is provided, append it to the message with two newlines
    let fullMessage = message;
    if (url) {
      fullMessage = `${message}\n\n${url}`;
    }
    
    const encodedMessage = encodeURIComponent(fullMessage);
    
    return `${baseURL}?text=${encodedMessage}`;
  }

  /**
   * Share to native WhatsApp app on mobile
   */
  private async shareToNativeApp(shareData: ShareData): Promise<boolean> {
    const whatsappURL = this.generateWhatsAppMobileURL(shareData.message, shareData.url);
    
    try {
      // Try to open native app
      window.location.href = whatsappURL;
      
      // Wait a moment to see if the app opened
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // If we're still here, the native app might not be available
      // Fall back to WhatsApp Web
      return await this.shareToWhatsAppWeb(shareData);
    } catch (error) {
      console.error('Native WhatsApp sharing failed:', error);
      return await this.shareToWhatsAppWeb(shareData);
    }
  }

  /**
   * Share to WhatsApp Web
   */
  private async shareToWhatsAppWeb(shareData: ShareData): Promise<boolean> {
    try {
      const whatsappWebURL = this.generateWhatsAppWebURL(shareData.message, shareData.url);
      
      // Open WhatsApp Web in a new window/tab
      const whatsappWindow = window.open(whatsappWebURL, '_blank', 'noopener,noreferrer');
      
      if (whatsappWindow) {
        // Focus the new window
        whatsappWindow.focus();
        return true;
      } else {
        // Popup blocked, fall back to other methods
        return false;
      }
    } catch (error) {
      console.error('WhatsApp Web sharing failed:', error);
      return false;
    }
  }

  /**
   * Handle sharing fallback when WhatsApp is not available
   */
  private async handleSharingFallback(shareData: ShareData): Promise<boolean> {
    try {
      // Try Web Share API first (if available)
      if (navigator.share && this.isWebShareSupported()) {
        await navigator.share({
          title: shareData.title || 'PlayHub Games AI',
          text: shareData.message,
          url: shareData.url
        });
        return true;
      }

      // Fall back to clipboard copy
      return await this.copyToClipboard(shareData);
    } catch (error) {
      console.error('Fallback sharing failed:', error);
      return await this.copyToClipboard(shareData);
    }
  }

  /**
   * Copy share content to clipboard as last resort
   */
  private async copyToClipboard(shareData: ShareData): Promise<boolean> {
    try {
      // Message already contains URL and "Powered by" text from ShareModal
      const fullMessage = shareData.message;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullMessage);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = fullMessage;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        return successful;
      }
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      return false;
    }
  }

  /**
   * Check if Web Share API is supported and available
   */
  private isWebShareSupported(): boolean {
    return 'share' in navigator && typeof navigator.share === 'function';
  }

  /**
   * Get sharing method name for display purposes
   */
  public getSharingMethodName(method: SharingMethod): string {
    switch (method) {
      case SharingMethod.WHATSAPP_NATIVE:
        return 'WhatsApp App';
      case SharingMethod.WHATSAPP_WEB:
        return 'WhatsApp Web';
      case SharingMethod.GENERIC_SHARE:
        return 'Share';
      case SharingMethod.CLIPBOARD_COPY:
        return 'Copy to Clipboard';
      default:
        return 'Share';
    }
  }

  /**
   * Test WhatsApp connectivity (for debugging)
   */
  public async testWhatsAppConnectivity(): Promise<{
    nativeAppAvailable: boolean;
    webAccessible: boolean;
    preferredMethod: SharingMethod;
  }> {
    const availability = this.detectWhatsAppAvailability();
    
    return {
      nativeAppAvailable: availability.hasNativeApp,
      webAccessible: availability.hasWebAccess,
      preferredMethod: availability.preferredMethod
    };
  }
}

// Export singleton instance
export const whatsappService = WhatsAppService.getInstance();